import React, { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import { DetailCard } from './DetailCard';
import { RiderDetails } from './RiderDetails';
import { TenantDetails } from './TenantDetails';
import { useDispatch, useSelector } from 'react-redux';
import { openDetails } from 'store/actions/uiActions';
import { getPolicyDetailAction } from 'store/actions/propertyActions';
import { useHydrateDetailsOnce } from 'hooks/useHydrateDetailsOnce';
import { maskDemoField } from 'utils/demoMask';
import { DetailPageShell, SectionPanelSkeleton } from 'components/DetailPageState';
import { useDetailPageState } from 'hooks/useDetailPageState';
import { useImmutableJs } from 'hooks/useImmutableJs';

export const FlaggedCancellationDetail = (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { policyId } = useParams();

  const isDemo = useSelector((state) => state.getIn(['ui', 'demo'])) || false;
  const details = useSelector((state) => state.getIn(['ui', 'details'])) || null;
  const detailsJSON = useImmutableJs(details);
  const policyDetail = useSelector((state) => state.getIn(['property', 'policy-detail'])) || null;
  const policyDetailJSON = useImmutableJs(policyDetail);
  const policyFromApi = policyDetailJSON?.data || null;
  const hasUiFallback =
    !policyId && detailsJSON?.type === 'flagged-cancellation' && detailsJSON?.data;
  const {
    data: application,
    isLoading,
    hasError,
  } = useDetailPageState({
    routeId: policyId,
    detailRecord: policyDetailJSON,
    hasUiFallback,
    fallbackData: detailsJSON?.data || null,
  });

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    navigate('/property/cancel-policies');
  }, [dispatch, navigate]);

  const onRetry = useCallback(() => {
    if (policyId) dispatch(getPolicyDetailAction(policyId));
  }, [policyId, dispatch]);

  useEffect(() => {
    if (policyId) {
      dispatch(getPolicyDetailAction(policyId));
      return;
    }

    if (!details || detailsJSON?.type !== 'flagged-cancellation') {
      onClickBack();
    }
  }, [policyId, dispatch, onClickBack, details, detailsJSON]);

  useHydrateDetailsOnce({
    type: 'flagged-cancellation',
    data: policyFromApi,
    id: policyFromApi?.app_id,
  });

  return (
    <DashboardLayoutContainer shouldShowCard={false}>
      <DetailPageShell
        onBack={onClickBack}
        title={application ? maskDemoField(isDemo, 'tenant-name', application.name) : null}
        isLoading={isLoading}
        hasError={hasError}
        onRetry={onRetry}
        loadingLabel="Loading cancellation details..."
        skeletonVariant="fields"
      >
        {application ? <DetailCard application={application} isDemo={isDemo} /> : null}
      </DetailPageShell>
      {isLoading ? (
        <>
          <SectionPanelSkeleton />
          <SectionPanelSkeleton />
        </>
      ) : hasError || !application ? null : (
        <>
          <Box borderRadius="12px" sx={{ border: '1px solid #EAEAEA', background: 'white' }} mt={1}>
            <RiderDetails application={application} isDemo={isDemo} />
          </Box>
          <Box borderRadius="12px" sx={{ border: '1px solid #EAEAEA', background: 'white' }} mt={1}>
            <TenantDetails application={application} isDemo={isDemo} />
          </Box>
        </>
      )}
    </DashboardLayoutContainer>
  );
};
